/**
 * Integrytas Group - Common Security Module (CSM)
 * Contextual AI Assistance — React hook that resolves a screen's AI config.
 *
 * Returns the effective per-screen configuration (folding in the global
 * CSM.AI.ENABLED switch) so a page can decide whether to render the Smart Query
 * / Smart Paste affordances at all. Deny-by-default: until the config loads, or
 * if anything fails, the screen is treated as AI-disabled.
 */

import { useEffect, useState } from 'react';
import { aiClient } from './ai_client';
import { AI_DISABLED_CONFIG, type AiScreenConfig } from './types';

export interface UseAiAssistResult {
  config:  AiScreenConfig;
  loading: boolean;
}

export function useAiAssist(pageCode: string): UseAiAssistResult {
  const [config, setConfig] = useState<AiScreenConfig>({ ...AI_DISABLED_CONFIG, page_code: pageCode });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    aiClient.getConfig(pageCode)
      .then((cfg) => { if (!cancelled) setConfig(cfg); })
      .catch(() => { if (!cancelled) setConfig({ ...AI_DISABLED_CONFIG, page_code: pageCode }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [pageCode]);

  return { config, loading };
}
